import { useState } from 'react';
import { Loader2, UserPlus, Trash2, Shield, Users } from 'lucide-react';
import {
  useEnvironmentPermissions,
  useSetPermission,
  useDeletePermission,
  PERMISSION_ROLES,
  getRoleBadgeVariant,
  type PermissionRole,
  type EnvironmentPermission,
} from '../../hooks/usePermissions';
import { useTeamMembers } from '../../hooks/useTeams';
import { useToast } from '@/components/ui/toast';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';

interface EnvironmentPermissionsModalProps {
  isOpen: boolean;
  onClose: () => void;
  projectId: string;
  environmentName: string;
  teamId: string | null;
}

export function EnvironmentPermissionsModal({
  isOpen,
  onClose,
  projectId,
  environmentName,
  teamId,
}: EnvironmentPermissionsModalProps) {
  const toast = useToast();
  const [isAddingUser, setIsAddingUser] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<string>('');
  const [selectedRole, setSelectedRole] = useState<PermissionRole>('read');
  const [editingPermission, setEditingPermission] = useState<string | null>(null);
  const [editRole, setEditRole] = useState<PermissionRole>('read');

  // Fetch environment permissions
  const {
    data: permissionsData,
    isLoading: isLoadingPermissions,
    error: permissionsError,
  } = useEnvironmentPermissions(projectId, environmentName);

  // Fetch team members for the add user dropdown
  const { members: teamMembers, isLoading: isLoadingMembers } = useTeamMembers(teamId);

  // Mutations
  const setPermissionMutation = useSetPermission(projectId, environmentName);
  const deletePermissionMutation = useDeletePermission(projectId, environmentName);

  // Reset state when modal closes via onOpenChange handler
  const handleOpenChange = (open: boolean) => {
    if (!open) {
      setIsAddingUser(false);
      setSelectedUserId('');
      setSelectedRole('read');
      setEditingPermission(null);
      onClose();
    }
  };

  const permissions = permissionsData?.permissions ?? [];

  // Filter out users who already have permissions
  const availableMembers = teamMembers.filter(
    (member) => !permissions.some((p) => p.user_id === member.user_id)
  );

  const handleAddPermission = async () => {
    if (!selectedUserId) {
      toast.error('Please select a user');
      return;
    }

    try {
      await setPermissionMutation.mutateAsync({
        userId: selectedUserId,
        role: selectedRole,
      });
      toast.success('Permission added');
      setIsAddingUser(false);
      setSelectedUserId('');
      setSelectedRole('read');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to add permission');
    }
  };

  const handleUpdatePermission = async (userId: string) => {
    try {
      await setPermissionMutation.mutateAsync({
        userId,
        role: editRole,
      });
      toast.success('Permission updated');
      setEditingPermission(null);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to update permission');
    }
  };

  const handleDeletePermission = async (userId: string) => {
    try {
      await deletePermissionMutation.mutateAsync(userId);
      toast.success('Permission removed');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to remove permission');
    }
  };

  const startEditing = (permission: EnvironmentPermission) => {
    setEditingPermission(permission.user_id);
    setEditRole(permission.role);
  };

  const cancelEditing = () => {
    setEditingPermission(null);
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-lg max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5 text-primary" />
            Manage Access
          </DialogTitle>
          <DialogDescription>
            Control who can access the <span className="font-medium text-foreground">{environmentName}</span> environment
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 min-h-0">
          {/* Add user section */}
          {!isAddingUser ? (
            <Button
              variant="outline"
              className="w-full mb-4"
              onClick={() => setIsAddingUser(true)}
              disabled={availableMembers.length === 0}
            >
              <UserPlus className="h-4 w-4 mr-2" />
              {availableMembers.length === 0 ? 'All team members have access' : 'Add Team Member'}
            </Button>
          ) : (
            <div className="p-4 border border-border rounded-lg mb-4 space-y-4 bg-muted/30">
              <div className="space-y-2">
                <Label htmlFor="user-select">Team Member</Label>
                <select
                  id="user-select"
                  value={selectedUserId}
                  onChange={(e) => setSelectedUserId(e.target.value)}
                  className="flex h-9 w-full rounded-md border border-border bg-input px-3 text-sm focus-visible:outline-none focus-visible:border-primary/50 focus-visible:ring-2 focus-visible:ring-ring/20"
                  disabled={isLoadingMembers}
                >
                  <option value="">Select a team member...</option>
                  {availableMembers.map((member) => (
                    <option key={member.user_id} value={member.user_id}>
                      {member.email}
                    </option>
                  ))}
                </select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="role-select">Role</Label>
                <select
                  id="role-select"
                  value={selectedRole}
                  onChange={(e) => setSelectedRole(e.target.value as PermissionRole)}
                  className="flex h-9 w-full rounded-md border border-border bg-input px-3 text-sm focus-visible:outline-none focus-visible:border-primary/50 focus-visible:ring-2 focus-visible:ring-ring/20"
                >
                  {PERMISSION_ROLES.filter((r) => r.value !== 'none').map((role) => (
                    <option key={role.value} value={role.value}>
                      {role.label} - {role.description}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setIsAddingUser(false);
                    setSelectedUserId('');
                  }}
                >
                  Cancel
                </Button>
                <Button
                  size="sm"
                  onClick={handleAddPermission}
                  disabled={!selectedUserId || setPermissionMutation.isPending}
                >
                  {setPermissionMutation.isPending && (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  )}
                  Add Permission
                </Button>
              </div>
            </div>
          )}

          {/* Permissions list */}
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm text-muted-foreground mb-3">
              <Users className="h-4 w-4" />
              <span>Current Permissions</span>
              <span className="text-xs bg-muted px-2 py-0.5 rounded-full">
                {permissions.length}
              </span>
            </div>

            {isLoadingPermissions ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : permissionsError ? (
              <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-lg text-sm text-destructive">
                Failed to load permissions
              </div>
            ) : permissions.length === 0 ? (
              <div className="p-4 text-center text-sm text-muted-foreground border border-dashed border-border rounded-lg">
                No permissions set. Team admins have full access by default.
              </div>
            ) : (
              <ScrollArea className="h-[300px] pr-4">
                <div className="space-y-2">
                  {permissions.map((permission) => (
                    <div
                      key={permission.id}
                      className="flex items-center justify-between p-3 border border-border rounded-lg hover:bg-accent/50 transition-colors"
                    >
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-sm truncate">
                          {permission.user_name || permission.user_email}
                        </div>
                        {permission.user_name && (
                          <div className="text-xs text-muted-foreground truncate">
                            {permission.user_email}
                          </div>
                        )}
                      </div>

                      {editingPermission === permission.user_id ? (
                        <div className="flex items-center gap-2 ml-4">
                          <select
                            value={editRole}
                            onChange={(e) => setEditRole(e.target.value as PermissionRole)}
                            className="h-8 rounded-md border border-border bg-input px-2 text-sm focus-visible:outline-none focus-visible:border-primary/50"
                          >
                            {PERMISSION_ROLES.map((role) => (
                              <option key={role.value} value={role.value}>
                                {role.label}
                              </option>
                            ))}
                          </select>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={cancelEditing}
                            className="h-8 px-2"
                          >
                            Cancel
                          </Button>
                          <Button
                            size="sm"
                            onClick={() => handleUpdatePermission(permission.user_id)}
                            disabled={setPermissionMutation.isPending}
                            className="h-8 px-2"
                          >
                            {setPermissionMutation.isPending ? (
                              <Loader2 className="h-3 w-3 animate-spin" />
                            ) : (
                              'Save'
                            )}
                          </Button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2 ml-4">
                          <Badge
                            variant={getRoleBadgeVariant(permission.role)}
                            className="cursor-pointer"
                            onClick={() => startEditing(permission)}
                          >
                            {PERMISSION_ROLES.find((r) => r.value === permission.role)?.label || permission.role}
                          </Badge>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-muted-foreground hover:text-destructive"
                            onClick={() => handleDeletePermission(permission.user_id)}
                            disabled={deletePermissionMutation.isPending}
                          >
                            {deletePermissionMutation.isPending ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <Trash2 className="h-4 w-4" />
                            )}
                          </Button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </ScrollArea>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Done
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
